import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE, jsonHeaders } from '../../utils/api';

const emptyCalendar = {
  _id: null,
  courseName: '',
  semesterType: '',
  semesterName: '',
  academicYear: '',
  columns: [
    { key: 'date', label: 'Date' },
    { key: 'event', label: 'Event' },
    { key: 'remarks', label: 'Remarks' },
  ],
  cells: [],
};

const AcademicCalendar = () => {
  const [calendars, setCalendars] = useState([]);
  const [form, setForm] = useState(emptyCalendar);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const role = useMemo(() => {
    try {
      const raw = localStorage.getItem('adminUser');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed.role || null;
    } catch {
      return null;
    }
  }, []);

  const fetchCalendars = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/academic-calendar`,
        {
          headers: jsonHeaders(),
        }
      );
      if (!res.ok) throw new Error('Failed to load calendars');
      const data = await res.json();
      setCalendars(data);
    } catch (err) {
      setError(err.message || 'Error loading calendars');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendars();
  }, []);

  const resetForm = () => {
    setForm(emptyCalendar);
    setSelectedId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCellChange = (rowIndex, colIndex, value) => {
    setForm((prev) => {
      const cells = [...prev.cells];
      const existingIndex = cells.findIndex(
        (c) => c.rowIndex === rowIndex && c.colIndex === colIndex
      );
      if (existingIndex >= 0) {
        cells[existingIndex] = { ...cells[existingIndex], value };
      } else {
        cells.push({ rowIndex, colIndex, value });
      }
      return { ...prev, cells };
    });
  };

  const addRow = () => {
    const currentRows =
      form.cells.length === 0
        ? 0
        : Math.max(...form.cells.map((c) => c.rowIndex)) + 1;
    // Just touch one cell to create a new row visually
    handleCellChange(currentRows, 0, '');
  };

  const onEdit = (cal) => {
    setSelectedId(cal._id);
    setForm({
      ...emptyCalendar,
      ...cal,
    });
  };

  const saveCalendar = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        courseName: form.courseName,
        semesterType: form.semesterType,
        semesterName: form.semesterName,
        academicYear: form.academicYear,
        columns: form.columns,
        cells: form.cells,
      };

      const method = selectedId ? 'PUT' : 'POST';
      const url = selectedId
        ? `${API_BASE}/api/admin/academic-calendar/${selectedId}`
        : `${API_BASE}/api/admin/academic-calendar`;

      const res = await fetch(url, {
        method,
        headers: jsonHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const msg = await res.json().catch(() => ({}));
        throw new Error(msg.message || 'Failed to save calendar');
      }
      await fetchCalendars();
      setSuccess('Calendar saved successfully');
      resetForm();
    } catch (err) {
      setError(err.message || 'Error saving calendar');
    } finally {
      setSaving(false);
    }
  };

  const deleteCalendar = async (id) => {
    if (!window.confirm('Delete this calendar?')) return;
    setError('');
    setSuccess('');
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/academic-calendar/${id}`,
        {
          method: 'DELETE',
          headers: jsonHeaders(),
        }
      );
      if (!res.ok) {
        const msg = await res.json().catch(() => ({}));
        throw new Error(msg.message || 'Failed to delete calendar');
      }
      await fetchCalendars();
      setSuccess('Calendar deleted');
    } catch (err) {
      setError(err.message || 'Error deleting calendar');
    }
  };

  const postAction = async (id, action, extraBody) => {
    setError('');
    setSuccess('');
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/academic-calendar/${id}/${action}`,
        {
          method: 'POST',
          headers: jsonHeaders(),
          body: extraBody ? JSON.stringify(extraBody) : undefined,
        }
      );
      if (!res.ok) {
        const msg = await res.json().catch(() => ({}));
        throw new Error(msg.message || `Failed to ${action}`);
      }
      await fetchCalendars();
      setSuccess(`Action ${action} completed`);
    } catch (err) {
      setError(err.message || 'Error performing action');
    }
  };

  const isSuperadmin = role === 'superadmin';

  return (
    <div className="p-4">
      <h1>Academic Calendar Management</h1>
      <p>
        Create and manage academic calendars. Both Web Coordinator and
        Superadmin can create and edit; only Superadmin can approve and make
        calendars live.
      </p>

      {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
      {success && (
        <div style={{ color: 'green', marginBottom: 8 }}>{success}</div>
      )}

      <section style={{ marginBottom: 24, border: '1px solid #ddd', padding: 12 }}>
        <h2>{selectedId ? 'Edit Calendar' : 'Create New Calendar'}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <label>
            Course Name
            <input
              type="text"
              name="courseName"
              value={form.courseName}
              onChange={handleChange}
            />
          </label>
          <label>
            Semester Type
            <select
              name="semesterType"
              value={form.semesterType}
              onChange={handleChange}
            >
              <option value="">Select</option>
              <option value="Odd">Odd</option>
              <option value="Even">Even</option>
              <option value="Summer">Summer</option>
              <option value="Other">Other</option>
            </select>
          </label>
          <label>
            Semester Name
            <input
              type="text"
              name="semesterName"
              value={form.semesterName}
              onChange={handleChange}
              placeholder="e.g., Semester I"
            />
          </label>
          <label>
            Academic Year
            <input
              type="text"
              name="academicYear"
              value={form.academicYear}
              onChange={handleChange}
              placeholder="e.g., 2025-2026"
            />
          </label>
        </div>

        <div style={{ marginTop: 16 }}>
          <h3>Calendar Table</h3>
          <button type="button" onClick={addRow}>
            Add Row
          </button>
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table border="1" cellPadding="4" cellSpacing="0">
              <thead>
                <tr>
                  {form.columns.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const maxRow =
                    form.cells.length === 0
                      ? -1
                      : Math.max(...form.cells.map((c) => c.rowIndex));
                  const rows = [];
                  for (let r = 0; r <= maxRow; r += 1) {
                    rows.push(
                      <tr key={r}>
                        {form.columns.map((col, cIndex) => {
                          const cell =
                            form.cells.find(
                              (x) =>
                                x.rowIndex === r && x.colIndex === cIndex
                            ) || {};
                          return (
                            <td key={col.key}>
                              <input
                                type="text"
                                value={cell.value || ''}
                                onChange={(e) =>
                                  handleCellChange(
                                    r,
                                    cIndex,
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  }
                  return rows.length ? rows : (
                    <tr>
                      <td colSpan={form.columns.length}>
                        No rows yet. Click &quot;Add Row&quot;.
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <button type="button" onClick={saveCalendar} disabled={saving}>
            {saving ? 'Saving...' : 'Save Calendar'}
          </button>
          <button type="button" onClick={resetForm}>
            Clear
          </button>
        </div>
      </section>

      <section>
        <h2>Existing Calendars</h2>
        {loading ? (
          <p>Loading...</p>
        ) : calendars.length === 0 ? (
          <p>No calendars yet.</p>
        ) : (
          <table border="1" cellPadding="4" cellSpacing="0" width="100%">
            <thead>
              <tr>
                <th>Heading</th>
                <th>Course</th>
                <th>Semester</th>
                <th>Academic Year</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {calendars.map((cal) => (
                <tr key={cal._id}>
                  <td>{cal.calendarHeading}</td>
                  <td>{cal.courseName}</td>
                  <td>{cal.semesterName}</td>
                  <td>{cal.academicYear}</td>
                  <td>{cal.status}</td>
                  <td>
                    <button type="button" onClick={() => onEdit(cal)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteCalendar(cal._id)}
                    >
                      Delete
                    </button>
                    {(cal.status === 'draft' || cal.status === 'rejected') && (
                      <button
                        type="button"
                        onClick={() =>
                          postAction(cal._id, 'submit')
                        }
                      >
                        Submit for Approval
                      </button>
                    )}
                    {isSuperadmin && cal.status === 'pending' && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            postAction(cal._id, 'approve')
                          }
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const reason =
                              window.prompt(
                                'Reason for rejection?'
                              ) || '';
                            postAction(cal._id, 'reject', {
                              reason,
                            });
                          }}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {isSuperadmin &&
                      (cal.status === 'approved' ||
                        cal.status === 'live') && (
                        <button
                          type="button"
                          onClick={() =>
                            postAction(cal._id, 'publish')
                          }
                        >
                          Make Live
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default AcademicCalendar;